/** Back office → Leads (C3): Kanban pipeline or list, new lead, lead sheet with history and notes.
 * Design v2.1: large-title header, a toolbar (search, board / list), cards with an avatar. */

import {
  type ActivityKindEnum,
  type Lead,
  type LostReasonEnum,
  type SourceEnum,
  type StageEnum,
  useCatalogServicesList,
  useCrmContactsList,
  useCrmContactsReferralCodeCreate,
  useCrmLeadsActivitiesCreate,
  useCrmLeadsActivitiesList,
  useCrmLeadsCreate,
  useCrmLeadsHistoryList,
  useCrmLeadsList,
  useCrmLeadsMoveCreate,
  useCrmMembersList,
} from "@agencyos/api-client";
import { formatPhone } from "@agencyos/core";
import {
  Avatar,
  Badge,
  type BadgeTone,
  Button,
  EmptyState,
  GlassCard,
  GlassSheet,
  SegmentedControl,
  SelectField,
  Skeleton,
  SoftToggle,
  TextAreaField,
  TextField,
} from "@agencyos/ui-web";
import { Plus, Search, UserPlus, UsersRound } from "lucide-react";
import { useQueryClient } from "@tanstack/react-query";
import { type DragEvent, type FormEvent, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { Page } from "../components/AppShell";
import { localized } from "../components/TranslatedFields";
import { fieldErrors, useCurrentRole } from "../lib/settings";
import { NewClientSheet } from "./clients/NewClientSheet";

const STAGES: StageEnum[] = ["new", "qualified", "appointment", "assessment", "contract_sent", "client", "lost"];
const SOURCES: SourceEnum[] = ["whatsapp", "facebook", "instagram", "website", "referral", "walk_in", "phone", "other"];
const LOST: LostReasonEnum[] = ["price", "not_eligible", "no_answer", "competitor", "postponed", "other"];
const KINDS: ActivityKindEnum[] = ["note", "call", "whatsapp", "meeting", "email", "task"];
const STAGE_TONE: Record<StageEnum, BadgeTone> = {
  new: "default",
  qualified: "info",
  appointment: "accent",
  assessment: "warning",
  contract_sent: "offer",
  client: "success",
  lost: "danger",
};
type View = "board" | "list" | "clients";

function useRefreshCrm() {
  const queryClient = useQueryClient();
  return () =>
    void queryClient.invalidateQueries({
      predicate: (query) => String(query.queryKey[0] ?? "").startsWith("/api/v1/crm/"),
    });
}

function phoneText(phone?: string | null): string {
  if (!phone) return "";
  try {
    return formatPhone(phone);
  } catch {
    return phone;
  }
}

function NewLeadSheet({ open, onClose }: { open: boolean; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshCrm();
  const services = useCatalogServicesList();
  const create = useCrmLeadsCreate();
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [phone, setPhone] = useState("");
  const [service, setService] = useState("");
  const [source, setSource] = useState<SourceEnum>("whatsapp");
  const [consent, setConsent] = useState(false);
  const [consentError, setConsentError] = useState(false);
  const errors = fieldErrors(create.error);
  const contactErrors = (create.error as { body?: { new_contact?: Record<string, string[]> } } | null)?.body
    ?.new_contact;

  const onSubmit = (event: FormEvent) => {
    event.preventDefault();
    if (!consent) {
      setConsentError(true);
      return;
    }
    setConsentError(false);
    create.mutate(
      {
        data: {
          new_contact: { first_name: firstName, last_name: lastName, phone_e164: phone, consent: true, consent_source: source === "walk_in" ? "office" : source === "whatsapp" ? "whatsapp" : "other" },
          service: service || null,
          source,
        },
      },
      {
        onSuccess: () => {
          refresh();
          setFirstName("");
          setLastName("");
          setPhone("");
          setConsent(false);
          onClose();
        },
      },
    );
  };
  const serviceOptions = [
    { value: "", label: t("crm.noService") },
    ...(services.data?.status === 200
      ? services.data.data.results.map((s) => ({ value: s.id, label: `${s.code} — ${localized(s.name, i18n.language)}` }))
      : []),
  ];
  return (
    <GlassSheet open={open} title={t("crm.newLead")} onClose={onClose}>
      <form onSubmit={onSubmit} className="stack" noValidate>
        <div className="grid-2">
          <TextField label={t("crm.fields.firstName")} value={firstName} onChange={(e) => setFirstName(e.target.value)} />
          <TextField label={t("crm.fields.lastName")} value={lastName} onChange={(e) => setLastName(e.target.value)} />
        </div>
        <TextField
          label={t("crm.fields.phone")}
          type="tel"
          inputMode="tel"
          value={phone}
          hint={t("agency.phoneHint")}
          error={contactErrors?.phone_e164?.[0]}
          onChange={(e) => setPhone(e.target.value)}
        />
        <SelectField label={t("crm.fields.service")} value={service} options={serviceOptions} onChange={(e) => setService(e.target.value)} />
        <SelectField
          label={t("crm.fields.source")}
          value={source}
          options={SOURCES.map((s) => ({ value: s, label: t(`crm.source.${s}`) }))}
          onChange={(e) => setSource(e.target.value as SourceEnum)}
        />
        <SoftToggle label={t("crm.consent")} checked={consent} onChange={setConsent} />
        {consentError ? (
          <p role="alert" className="ao-field__error">
            {t("crm.consentRequired")}
          </p>
        ) : null}
        {errors.non_field_errors ? <p role="alert" className="ao-field__error">{errors.non_field_errors}</p> : null}
        <div className="row">
          <Button type="submit" disabled={create.isPending}>
            {create.isPending ? t("common.loading") : t("crm.create")}
          </Button>
          <Button variant="ghost" onClick={onClose}>
            {t("common.cancel")}
          </Button>
        </div>
      </form>
    </GlassSheet>
  );
}

function LostSheet({ lead, onDone }: { lead: Lead | null; onDone: () => void }) {
  const { t } = useTranslation();
  const refresh = useRefreshCrm();
  const move = useCrmLeadsMoveCreate({ mutation: { onSuccess: refresh } });
  const [reason, setReason] = useState<LostReasonEnum>("price");
  const [note, setNote] = useState("");
  return (
    <GlassSheet open={lead !== null} title={t("crm.lostTitle")} onClose={onDone}>
      <div className="stack">
        <SelectField
          label={t("crm.fields.lostReason")}
          value={reason}
          options={LOST.map((r) => ({ value: r, label: t(`crm.lostReason.${r}`) }))}
          onChange={(e) => setReason(e.target.value as LostReasonEnum)}
        />
        <TextField label={t("crm.fields.lostNote")} value={note} maxLength={255} onChange={(e) => setNote(e.target.value)} />
        <div className="row">
          <Button
            disabled={move.isPending}
            onClick={() =>
              lead &&
              move.mutate(
                { id: lead.id, data: { stage: "lost", lost_reason: reason, lost_note: note } },
                { onSuccess: onDone },
              )
            }
          >
            {t("crm.confirmLost")}
          </Button>
          <Button variant="ghost" onClick={onDone}>
            {t("common.cancel")}
          </Button>
        </div>
      </div>
    </GlassSheet>
  );
}

function LeadSheet({ lead, onClose }: { lead: Lead | null; onClose: () => void }) {
  const { t, i18n } = useTranslation();
  const refresh = useRefreshCrm();
  const id = lead?.id ?? "";
  const history = useCrmLeadsHistoryList(id, { query: { enabled: Boolean(lead) } });
  const activities = useCrmLeadsActivitiesList(id, { query: { enabled: Boolean(lead) } });
  const add = useCrmLeadsActivitiesCreate({ mutation: { onSuccess: refresh } });
  const [kind, setKind] = useState<ActivityKindEnum>("note");
  const [body, setBody] = useState("");
  const referral = useCrmContactsReferralCodeCreate();
  const code = referral.data?.status === 200 ? referral.data.data.code : undefined;
  const locale = i18n.language === "en" ? "en-GB" : i18n.language;
  const when = (iso: string) =>
    new Date(iso).toLocaleString(locale, { timeZone: "Africa/Douala", dateStyle: "short", timeStyle: "short" });
  return (
    <GlassSheet open={lead !== null} title={t("crm.details")} onClose={onClose}>
      {lead ? (
        <div className="stack">
          <div className="lead-head">
            <Avatar name={lead.contact.full_name || lead.contact.phone_e164 || "?"} size="lg" />
            <div className="lead-head__who">
              <h3>{lead.contact.full_name || phoneText(lead.contact.phone_e164)}</h3>
              <p className="muted ao-num">{phoneText(lead.contact.phone_e164)}</p>
            </div>
            <Badge dot tone={STAGE_TONE[lead.stage ?? "new"]}>{t(`crm.stage.${lead.stage ?? "new"}`)}</Badge>
          </div>
          <dl className="facts">
            {lead.service_code ? (
              <div>
                <dt>{t("crm.columns.service")}</dt>
                <dd>
                  <span className="code-chip">{lead.service_code}</span>
                </dd>
              </div>
            ) : null}
            <div>
              <dt>{t("crm.columns.source")}</dt>
              <dd>{t(`crm.source.${lead.source ?? "other"}`)}</dd>
            </div>
            <div>
              <dt>{t("crm.columns.owner")}</dt>
              <dd>{lead.assigned_to_name || t("crm.unassigned")}</dd>
            </div>
            {lead.referred_by_name ? (
              <div>
                <dt>{t("crm.referralCode")}</dt>
                <dd>{t("crm.referredBy", { name: lead.referred_by_name })}</dd>
              </div>
            ) : null}
          </dl>
          <div className="row">
            <Link className="ao-button ao-button--secondary" to={`/clients/${lead.contact.id}`}>
              {t("clients.open")}
            </Link>
            <Button variant="secondary" disabled={referral.isPending} onClick={() => referral.mutate({ id: lead.contact.id })}>
              {t("crm.referralCode")}
            </Button>
            {code ? (
              <span role="status" className="code-chip">
                {code}
              </span>
            ) : null}
          </div>
          <section className="stack">
            <h4>{t("crm.activities")}</h4>
            <form
              className="stack"
              onSubmit={(e) => {
                e.preventDefault();
                if (!body.trim()) return;
                add.mutate({ id: lead.id, data: { kind, body } }, { onSuccess: () => setBody("") });
              }}
            >
              <SelectField
                label={t("crm.fields.kind")}
                value={kind}
                options={KINDS.map((k) => ({ value: k, label: t(`crm.activityKind.${k}`) }))}
                onChange={(e) => setKind(e.target.value as ActivityKindEnum)}
              />
              <TextAreaField label={t("crm.activityText")} rows={2} value={body} onChange={(e) => setBody(e.target.value)} />
              <div className="row">
                <Button type="submit" variant="secondary" disabled={add.isPending}>
                  {t("crm.addActivity")}
                </Button>
              </div>
            </form>
            <ol className="plain-list feed">
              {activities.data?.status === 200
                ? activities.data.data.map((a) => (
                    <li key={a.id} className="feed__item">
                      <span className="feed__dot" aria-hidden="true" />
                      <div className="feed__body">
                        <span className="row">
                          <Badge>{t(`crm.activityKind.${a.kind ?? "note"}`)}</Badge>
                          <span className="muted feed__meta">
                            {when(a.created_at)} {a.author_name ? `· ${a.author_name}` : ""}
                          </span>
                        </span>
                        <p>{a.body}</p>
                      </div>
                    </li>
                  ))
                : null}
            </ol>
          </section>
          <section className="stack">
            <h4>{t("crm.history")}</h4>
            <ul className="plain-list feed feed--compact">
              {history.data?.status === 200
                ? history.data.data.map((h) => (
                    <li key={h.id} className="muted feed__item">
                      {when(h.created_at)} · {h.from_stage ? t(`crm.stage.${h.from_stage}`) : ""} → {t(`crm.stage.${h.to_stage}`)}
                      {h.changed_by_name ? ` · ${h.changed_by_name}` : ""}
                    </li>
                  ))
                : null}
            </ul>
          </section>
        </div>
      ) : null}
    </GlassSheet>
  );
}

/** D-126: the clients (lead at the « client » stage, or invoiced, or under contract). */
function ClientList({ search }: { search: string }) {
  const { t } = useTranslation();
  const q = search.trim();
  const clients = useCrmContactsList({ clients: "1", ...(q ? { q } : {}) });
  const rows = clients.data?.status === 200 ? clients.data.data.results : [];
  if (clients.isPending) return <Skeleton style={{ height: 120 }} />;
  return (
    <GlassCard className="ao-card--flush">
      {rows.length ? (
        <div className="table-wrap crm-list">
          <table className="table">
            <thead>
              <tr>
                <th>{t("crm.columns.name")}</th>
                <th>{t("clients.fields.email")}</th>
                <th>{t("clients.fields.city")}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((client) => (
                <tr key={client.id}>
                  <td>
                    <span className="lead-cell">
                      <Avatar name={client.full_name || client.phone_e164 || "?"} />
                      <Link className="kanban__open" to={`/clients/${client.id}`}>
                        <strong>{client.full_name || phoneText(client.phone_e164)}</strong>
                        <span className="muted">{phoneText(client.phone_e164)}</span>
                      </Link>
                    </span>
                  </td>
                  <td className="muted">{client.email || "—"}</td>
                  <td className="muted">{client.city || "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <EmptyState icon={<UsersRound size={22} strokeWidth={1.75} />} title={t("clients.empty")} />
      )}
    </GlassCard>
  );
}

export function CrmPage() {
  const { t, i18n } = useTranslation();
  const role = useCurrentRole();
  const refresh = useRefreshCrm();
  const leads = useCrmLeadsList(undefined, { query: { staleTime: 5_000 } });
  const members = useCrmMembersList();
  const move = useCrmLeadsMoveCreate({ mutation: { onSuccess: refresh } });
  const [creating, setCreating] = useState(false);
  const [addingClient, setAddingClient] = useState(false);
  const [losing, setLosing] = useState<Lead | null>(null);
  const [opened, setOpened] = useState<Lead | null>(null);
  const [dragOver, setDragOver] = useState<StageEnum | null>(null);
  const [search, setSearch] = useState("");
  const [view, setView] = useState<View>("board");
  const canWork = role === "owner" || role === "manager" || role === "advisor";

  const moveTo = (lead: Lead, stage: StageEnum) => {
    if (stage === lead.stage) return;
    if (stage === "lost") setLosing(lead);
    else move.mutate({ id: lead.id, data: { stage } });
  };
  const all = leads.data?.status === 200 ? leads.data.data.results : [];
  const needle = search.trim().toLowerCase();
  const digits = needle.replace(/\D/g, "");
  const shown = all.filter(
    (lead) =>
      !needle ||
      lead.contact.full_name.toLowerCase().includes(needle) ||
      (digits.length >= 3 && (lead.contact.phone_e164 ?? "").includes(digits.slice(-9))),
  );
  const memberName = new Map(
    (members.data?.status === 200 ? members.data.data : []).map((m) => [String(m.id), m.full_name]),
  );

  const onDrop = (stage: StageEnum) => (event: DragEvent) => {
    event.preventDefault();
    setDragOver(null);
    const lead = all.find((l) => l.id === event.dataTransfer.getData("text/plain"));
    if (lead) moveTo(lead, stage);
  };

  const owner = (lead: Lead) =>
    lead.assigned_to ? memberName.get(String(lead.assigned_to)) ?? lead.assigned_to_name : t("crm.unassigned");
  const created = (iso: string) => new Date(iso).toLocaleDateString(i18n.language === "en" ? "en-GB" : i18n.language, { timeZone: "Africa/Douala" });

  return (
    <Page
      title={t("crm.title")}
      description={
        <>
          {t("crm.intro")}
          {role === "advisor" ? <> {t("crm.advisorScope")}</> : null}
        </>
      }
      actions={
        canWork ? (
          <>
            <Button icon={<UserPlus size={16} strokeWidth={2} aria-hidden="true" />} onClick={() => setAddingClient(true)}>
              {t("clients.new.open")}
            </Button>
            <Button variant="secondary" icon={<Plus size={16} strokeWidth={2} aria-hidden="true" />} onClick={() => setCreating(true)}>
              {t("crm.newLead")}
            </Button>
          </>
        ) : null
      }
    >
      <div className="crm-toolbar">
        <div className="crm-toolbar__search ao-input-icon">
          <Search size={15} strokeWidth={2} aria-hidden="true" />
          <input
            type="search"
            className="ao-field__input"
            aria-label={t("crm.search")}
            placeholder={t("crm.search")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
        </div>
        <SegmentedControl<View>
          label={t("crm.viewLabel")}
          value={view}
          options={[
            { value: "board", label: t("crm.view.board") },
            { value: "list", label: t("crm.view.list") },
            { value: "clients", label: t("crm.view.clients") },
          ]}
          onChange={setView}
        />
        {leads.data?.status === 200 ? (
          <span className="crm-toolbar__count muted ao-num">{t("crm.count", { count: shown.length })}</span>
        ) : null}
      </div>
      {leads.data?.status === 200 && leads.data.data.count > all.length ? (
        <p role="note" className="muted">
          {t("crm.truncated", { shown: all.length, count: leads.data.data.count })}
        </p>
      ) : null}
      {leads.isPending ? (
        <div className="kanban" aria-busy="true">
          {STAGES.slice(0, 4).map((stage) => (
            <div key={stage} className="kanban__column">
              <Skeleton style={{ height: 18, width: "50%" }} />
              <Skeleton style={{ height: 96, borderRadius: 16 }} />
              <Skeleton style={{ height: 96, borderRadius: 16 }} />
            </div>
          ))}
        </div>
      ) : view === "clients" ? (
        <ClientList search={search} />
      ) : view === "list" ? (
        <GlassCard className="ao-card--flush">
          {shown.length ? (
            <div className="table-wrap crm-list">
              <table className="table">
                <thead>
                  <tr>
                    <th>{t("crm.columns.name")}</th>
                    <th>{t("crm.columns.stage")}</th>
                    <th>{t("crm.columns.service")}</th>
                    <th>{t("crm.columns.source")}</th>
                    <th>{t("crm.columns.owner")}</th>
                    <th className="num">{t("crm.columns.created")}</th>
                  </tr>
                </thead>
                <tbody>
                  {shown.map((lead) => (
                    <tr key={lead.id}>
                      <td>
                        <span className="lead-cell">
                          <Avatar name={lead.contact.full_name || lead.contact.phone_e164 || "?"} />
                          <button type="button" className="kanban__open" onClick={() => setOpened(lead)}>
                            <strong>{lead.contact.full_name || phoneText(lead.contact.phone_e164)}</strong>
                            <span className="muted">{phoneText(lead.contact.phone_e164)}</span>
                          </button>
                        </span>
                      </td>
                      <td>
                        <Badge dot tone={STAGE_TONE[lead.stage ?? "new"]}>{t(`crm.stage.${lead.stage ?? "new"}`)}</Badge>
                      </td>
                      <td>{lead.service_code ? <span className="code-chip">{lead.service_code}</span> : <span className="muted">—</span>}</td>
                      <td className="muted">{t(`crm.source.${lead.source ?? "other"}`)}</td>
                      <td className="muted">{owner(lead)}</td>
                      <td className="num muted">
                        <time dateTime={lead.created_at}>{created(lead.created_at)}</time>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <EmptyState icon={<UsersRound size={22} strokeWidth={1.75} />} title={t("crm.empty")} />
          )}
        </GlassCard>
      ) : (
        <div className="kanban" role="list">
          {STAGES.map((stage) => {
            const cards = shown.filter((lead) => lead.stage === stage);
            return (
              <section
                key={stage}
                role="listitem"
                aria-label={t(`crm.stage.${stage}`)}
                data-stage={stage}
                className={`kanban__column${dragOver === stage ? " kanban__column--over" : ""}`}
                onDragOver={(e) => {
                  e.preventDefault();
                  setDragOver(stage);
                }}
                onDragLeave={() => setDragOver(null)}
                onDrop={onDrop(stage)}
              >
                <h2 className="kanban__title">
                  {t(`crm.stage.${stage}`)} <Badge>{cards.length}</Badge>
                </h2>
                {cards.length === 0 ? <p className="muted">{t("crm.empty")}</p> : null}
                {cards.map((lead) => (
                  <article
                    key={lead.id}
                    className="kanban__card"
                    draggable={canWork}
                    onDragStart={(e) => e.dataTransfer.setData("text/plain", lead.id)}
                    aria-label={lead.contact.full_name || phoneText(lead.contact.phone_e164)}
                  >
                    <div className="kanban__head">
                      <Avatar name={lead.contact.full_name || lead.contact.phone_e164 || "?"} />
                      <button type="button" className="kanban__open" onClick={() => setOpened(lead)}>
                        <strong>{lead.contact.full_name || phoneText(lead.contact.phone_e164)}</strong>
                        <span className="muted">{phoneText(lead.contact.phone_e164)}</span>
                      </button>
                    </div>
                    <div className="row kanban__meta">
                      {lead.service_code ? <span className="code-chip">{lead.service_code}</span> : null}
                      <Badge>{t(`crm.source.${lead.source ?? "other"}`)}</Badge>
                      {lead.referred_by_name ? <Badge tone="offer">{t("crm.referredBy", { name: lead.referred_by_name })}</Badge> : null}
                      <span className="muted kanban__owner">{owner(lead)}</span>
                    </div>
                    {canWork ? (
                      <SelectField
                        label={t("crm.moveTo")}
                        value={lead.stage}
                        options={STAGES.map((s) => ({ value: s, label: t(`crm.stage.${s}`) }))}
                        onChange={(e) => moveTo(lead, e.target.value as StageEnum)}
                      />
                    ) : null}
                  </article>
                ))}
              </section>
            );
          })}
        </div>
      )}
      <NewLeadSheet open={creating} onClose={() => setCreating(false)} />
      {addingClient ? <NewClientSheet open onClose={() => setAddingClient(false)} /> : null}
      <LostSheet lead={losing} onDone={() => setLosing(null)} />
      <LeadSheet lead={opened} onClose={() => setOpened(null)} />
    </Page>
  );
}
